(function (global) {
  'use strict';

  const owns = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
  const invalid = () => ({ valid: false, priceCents: null, variantId: null });

  function variantKey(value) {
    if (typeof value === 'number') {
      return Number.isSafeInteger(value) && value > 0 ? String(value) : null;
    }
    if (typeof value !== 'string') return null;
    const key = value.trim();
    return /^[1-9][0-9]*$/.test(key) ? key : null;
  }

  function axisPrice(tiers, measurement, tierMode) {
    if (!Array.isArray(tiers) || tiers.length === 0) return null;
    let previousUpper = 0;
    let price = null;
    for (const tier of tiers) {
      if (!tier || typeof tier.upper_cm !== 'number' || !Number.isFinite(tier.upper_cm)
        || tier.upper_cm <= previousUpper || !Number.isSafeInteger(tier.price_cents)
        || tier.price_cents < 0) return null;
      previousUpper = tier.upper_cm;
      if (tierMode === 'previous_until_next') {
        if (price === null || measurement >= tier.upper_cm) price = tier.price_cents;
      } else if (price === null && measurement <= tier.upper_cm) price = tier.price_cents;
    }
    return price;
  }

  function resolve(pricing, width, height, baseVariantId, optionSurchargeCents) {
    const surchargeCents = optionSurchargeCents === undefined ? 0 : optionSurchargeCents;
    if (!Number.isSafeInteger(surchargeCents) || surchargeCents < 0
      || !pricing || pricing.version !== 1 || pricing.rule !== 'add_axis_surcharges'
      || (pricing.tier_mode !== undefined && !['upper_tier', 'previous_until_next'].includes(pricing.tier_mode))
      || !Number.isSafeInteger(pricing.base_price_cents) || pricing.base_price_cents < 0
      || typeof width !== 'number' || typeof height !== 'number'
      || !Number.isFinite(width) || !Number.isFinite(height)
      || width <= 0 || height <= 0) return invalid();

    const bounds = pricing.bounds;
    if (!bounds || !['width_min_cm', 'width_max_cm', 'height_min_cm', 'height_max_cm']
      .every((key) => typeof bounds[key] === 'number' && Number.isFinite(bounds[key])
        && bounds[key] >= 0)
      || bounds.width_min_cm > bounds.width_max_cm
      || bounds.height_min_cm > bounds.height_max_cm
      || width < bounds.width_min_cm || width > bounds.width_max_cm
      || height < bounds.height_min_cm || height > bounds.height_max_cm) return invalid();

    const widthPrice = axisPrice(pricing.width_tiers, width, pricing.tier_mode);
    const heightPrice = axisPrice(pricing.height_tiers, height, pricing.tier_mode);
    if (widthPrice === null || heightPrice === null) return invalid();

    const priceCents = widthPrice - pricing.base_price_cents + heightPrice + surchargeCents;
    if (!Number.isSafeInteger(priceCents) || priceCents < 0) return invalid();
    const baseKey = variantKey(baseVariantId);
    const map = pricing.variant_map;
    if (baseKey === null || !map || typeof map !== 'object' || !owns(map, baseKey)) return invalid();
    const variants = map[baseKey];
    const priceKey = String(priceCents);
    if (!variants || typeof variants !== 'object' || !owns(variants, priceKey)) return invalid();
    const variantId = variantKey(variants[priceKey]);
    return variantId === null ? invalid() : { valid: true, priceCents, variantId };
  }

  function completeVariants(pricing, nativeVariants) {
    if (!Array.isArray(nativeVariants)) return [];
    const completed = [...nativeVariants];
    if (!pricing || pricing.version !== 1 || pricing.rule !== 'add_axis_surcharges'
      || !pricing.variant_map || typeof pricing.variant_map !== 'object') return completed;
    const nativeById = new Map(nativeVariants.map((variant) => [variantKey(variant?.id), variant]));
    const knownIds = new Set(nativeById.keys());
    const priceTemplates = new Map();
    Object.values(pricing.variant_map).forEach((bands) => {
      if (!bands || typeof bands !== 'object') return;
      Object.entries(bands).forEach(([sourcePrice, id]) => {
        const template = nativeById.get(variantKey(id));
        if (!/^(0|[1-9][0-9]*)$/.test(sourcePrice) || !Number.isSafeInteger(Number(sourcePrice))
          || !template || !Number.isSafeInteger(template.price) || template.price < 0
          || typeof template.money !== 'string' || template.money === '') return;
        const existing = priceTemplates.get(sourcePrice);
        if (!existing || (!existing.available && template.available)) priceTemplates.set(sourcePrice, template);
      });
    });
    Object.entries(pricing.variant_map).forEach(([baseId, bands]) => {
      const base = nativeById.get(variantKey(baseId));
      if (!base || !bands || typeof bands !== 'object') return;
      Object.entries(bands).forEach(([sourcePrice, id]) => {
        const variantId = variantKey(id);
        const template = priceTemplates.get(sourcePrice);
        if (!variantId || knownIds.has(variantId) || !template) return;
        completed.push({
          id: variantId,
          title: base.title,
          available: base.available === true && template.available === true,
          price: template.price,
          money: template.money,
          mediaId: base.mediaId
        });
        knownIds.add(variantId);
      });
    });
    return completed;
  }

  global.NSMeasurePricing = { resolve, completeVariants };
})(window);
